import { Ability } from '@casl/ability';
import {
  useDeleteScheduleByExternalId,
  useGetTimeZones,
} from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  Actions,
  EditScheduleForm,
  editScheduleFormDefault,
  QueryResult,
  Schedule,
  Subjects,
} from '@dispatch/shared/types';
import { isRunningInJest } from '@dispatch/testing';
import {
  ConfirmModal,
  dateColumn,
  EMPTY_VALUE,
  Section,
  tableStyleOverrides,
  textColumn,
  useNotification,
  MUI_X_5_GRID_PROPS,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import { Stack } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridRowParams,
  GridColDef,
} from '@mui/x-data-grid-pro';
import React, { useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { getTimeWithOffset } from '@dispatch/shared';
import { useQueryClient } from 'react-query';

export type ResourceScheduleProps = {
  schedulesQuery?: QueryResult<Schedule[] | undefined, unknown>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  setEditScheduleForm: (editScheduleForm: EditScheduleForm) => void;
  setOpenEditSchedule: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  isReadOnly?: boolean;
};

export default function ResourceSchedule({
  schedulesQuery,
  permissions,
  setEditScheduleForm,
  setOpenEditSchedule,
  setIsEdit,
  isReadOnly = false,
}: ResourceScheduleProps) {
  const apiRef = useGridApiRefKeepingPage();
  const onClickEditSchedule = (editSchduleForm: EditScheduleForm) => {
    setIsEdit(true);
    setEditScheduleForm(editSchduleForm);
    setOpenEditSchedule(true);
  };

  const createScheduleForm = (
    startDateTime: Date,
    endDateTime: Date,
    externalId: string,
    timeZoneId: string,
    dailyClaimOwnerTasksCount: number
  ): EditScheduleForm => {
    return {
      ...editScheduleFormDefault,
      startDateTime: getTimeWithOffset(timeZoneId, startDateTime),
      endDateTime: getTimeWithOffset(timeZoneId, endDateTime),
      timeZoneId,
      externalId,
      dailyClaimOwnerTasksCount,
    };
  };
  const hasUpsertPermissions =
    permissions?.can?.('upsert', 'resource-schedules') || false;

  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const { notifyError, notifySuccess } = useNotification();
  const [toDelete, setToDelete] = useState<string>('');

  const { tenantId } = useCurrentUser();
  const { timeZones } = useGetTimeZones(tenantId || '');

  const getTimezoneFromTimeZoneId = (timeZoneId: string) => {
    return (
      timeZones.find((item) => item.value === timeZoneId)?.title || 'unknown'
    );
  };

  const queryClient = useQueryClient();
  const onDeleteSchedule = (response: { success: boolean }) => {
    if (response?.success) {
      notifySuccess('Success', 'Schedule was deleted');
      schedulesQuery?.refetch && schedulesQuery?.refetch();
      queryClient.invalidateQueries(['schedule-data']);
    } else {
      notifyError('Error', 'Error deleting schedule');
    }

    setOpenConfirmDeleteModal(false);
  };

  const { deleteSchedule } = useDeleteScheduleByExternalId(onDeleteSchedule);

  const onDeleteClick = (externalId: string) => {
    if (externalId) {
      setOpenConfirmDeleteModal(true);
      setToDelete(externalId);
    } else {
      notifyError('Error', 'Error deleting event');
    }
  };

  const renderConfirmDelete = () => {
    return (
      <ConfirmModal
        onConfirm={() => deleteSchedule(toDelete)}
        onDeny={() => setOpenConfirmDeleteModal(false)}
        confirmTitle={'Delete Schedule?'}
        confirmContent={
          'Deleting the selected schedule will remove availability for this Resource'
        }
        modalType={'delete'}
      />
    );
  };

  const columns: GridColDef[] = [
    dateColumn(
      {
        field: 'workingDayStartTime',
        headerName: 'Start Time',
        disableColumnMenu: true,
        valueGetter: (_value, row) =>
          getTimeWithOffset(
            row?.timeZoneId,
            new Date(row?.workingDayStartTime)
          ) || EMPTY_VALUE,
      },
      'EEE MM/dd/yyyy p',
      'resource-schedule-row'
    ),
    dateColumn(
      {
        field: 'workingDayEndTime',
        sortable: false,
        disableColumnMenu: true,
        headerName: 'End Time',
        valueGetter: (_value, row) =>
          getTimeWithOffset(
            row?.timeZoneId,
            new Date(row?.workingDayEndTime)
          ) || EMPTY_VALUE,
      },
      'EEE MM/dd/yyyy p'
    ),
    textColumn({
      field: 'timeZoneId',
      sortable: false,
      headerName: 'Timezone',
      disableColumnMenu: true,
      valueGetter: (_value, row) => getTimezoneFromTimeZoneId(row?.timeZoneId),
    }),
    {
      field: 'actions',
      type: 'actions',
      disableColumnMenu: true,
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="DELETE"
          onClick={() => onDeleteClick(params.row?.externalId)}
          disabled={isReadOnly}
        />,
      ],
    },
  ];

  const resourceSchedules = React.useMemo(() => {
    return (
      schedulesQuery?.data?.filter((queryData) => {
        return queryData?.isVisible;
      }) || []
    );
  }, [schedulesQuery?.data]);

  return (
    <Section>
      <div style={{ height: '500px' }} data-testid="resource-schedules-list">
        <DataGridPro
          apiRef={apiRef}
          {...MUI_X_5_GRID_PROPS}
          disableVirtualization={isRunningInJest()}
          loading={schedulesQuery?.status === 'loading'}
          rows={resourceSchedules}
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Schedules
              </Stack>
            ),
            noResultsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                No Schedules Found
              </Stack>
            ),
          }}
          columns={columns}
          sx={tableStyleOverrides}
          getRowId={(row) => row.externalId}
          hideFooter={isRunningInJest()}
          hideFooterSelectedRowCount={true}
          disableRowSelectionOnClick={true}
          onRowClick={(value) => {
            hasUpsertPermissions &&
              !isReadOnly &&
              onClickEditSchedule(
                createScheduleForm(
                  value.row.workingDayStartTime,
                  value.row.workingDayEndTime,
                  value.row.externalId,
                  value.row.timeZoneId,
                  value?.row?.dailyClaimOwnerTasksCount || 0
                )
              );
          }}
          columnVisibilityModel={{
            actions: hasUpsertPermissions,
          }}
          pagination
          autoPageSize
        />
      </div>
      {openConfirmDeleteModal && renderConfirmDelete()}
    </Section>
  );
}
